import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Shield,
  Plus,
  ArrowLeft,
  Upload,
  FileText,
  Image,
  Video,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Calendar,
  Building2,
  ChevronRight,
  Wrench,
  RefreshCw,
  Camera,
  ImageIcon,
  ChevronLeft,
  X,
  ZoomIn,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  submitted: 'bg-slate-100 text-slate-700',
  under_review: 'bg-blue-100 text-blue-700',
  insurance_notified: 'bg-purple-100 text-purple-700',
  contractor_assigned: 'bg-amber-100 text-amber-700',
  repair_in_progress: 'bg-orange-100 text-orange-700',
  completed: 'bg-emerald-100 text-emerald-700',
  claim_closed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const STATUS_NAMES = {
  submitted: 'Submitted',
  under_review: 'Under Review',
  insurance_notified: 'Insurance Notified',
  contractor_assigned: 'Contractor Assigned',
  repair_in_progress: 'Repair In Progress',
  completed: 'Completed',
  claim_closed: 'Claim Closed',
  rejected: 'Rejected',
};

export default function InsuranceClaims() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [claimTypes, setClaimTypes] = useState([]);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showGalleryDialog, setShowGalleryDialog] = useState(false);
  const [galleryView, setGalleryView] = useState('grid'); // 'grid', 'compare', 'slideshow'
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [comparisonSlider, setComparisonSlider] = useState(50);

  const [newClaim, setNewClaim] = useState({
    unit_number: '',
    claim_type: '',
    incident_date: '',
    incident_description: '',
    incident_location: '',
    submitter_phone: '',
    estimated_value: '',
  });

  const token = localStorage.getItem('session_token');
  const hoaId = user?.hoa_id || localStorage.getItem('hoa_id') || 'demo_hoa';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get claim types
      const typesRes = await fetch(`${API_URL}/api/insurance/claim-types`, { 
        credentials: 'include' 
      });
      if (typesRes.ok) {
        setClaimTypes(await typesRes.json());
      }
      
      // Get my claims
      const claimsRes = await fetch(`${API_URL}/api/insurance/claims/my-claims`, { 
        credentials: 'include' 
      });
      if (claimsRes.ok) {
        setClaims(await claimsRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitClaim = async () => {
    if (!newClaim.unit_number || !newClaim.claim_type || !newClaim.incident_date || !newClaim.incident_description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/insurance/claims/submit?hoa_id=${hoaId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          ...newClaim,
          estimated_value: newClaim.estimated_value ? parseFloat(newClaim.estimated_value) : null
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Claim submitted! Reference: ${data.claim_reference}`);
        setShowSubmitDialog(false);
        setNewClaim({
          unit_number: '',
          claim_type: '',
          incident_date: '',
          incident_description: '',
          incident_location: '',
          submitter_phone: '',
          estimated_value: '',
        });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit claim');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewClaim = async (claim) => {
    try {
      const res = await fetch(`${API_URL}/api/insurance/claims/${claim.claim_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setSelectedClaim(await res.json());
        setShowDetailDialog(true);
      }
    } catch (error) {
      toast.error('Failed to load claim details');
    }
  };

  const handleFileUpload = async (e, uploadType) => {
    const file = e.target.files[0];
    if (!file || !selectedClaim) return;

    // Check photo size (800KB max)
    if ((uploadType === 'photo' || uploadType === 'repair_photo') && file.size > 800 * 1024) {
      toast.error('Photo must be less than 800KB. Please compress or resize your image.');
      return;
    }

    // Check video size (30MB max, ~60 seconds)
    if (uploadType === 'video' && file.size > 30 * 1024 * 1024) {
      toast.error('Video must be less than 30MB (max 60 seconds)');
      return;
    }

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/insurance/claims/${selectedClaim.claim_id}/upload/${uploadType}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (res.ok) {
        const displayType = uploadType.replace('_', ' ');
        toast.success(`${displayType.charAt(0).toUpperCase() + displayType.slice(1)} uploaded`);
        handleViewClaim(selectedClaim);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploadingFile(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getClaimTypeName = (code) => {
    const type = claimTypes.find(t => t.code === code);
    return type?.name || code;
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="insurance-claims">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <Shield className="w-8 h-8 text-indigo-600" />
                Insurance Claims
              </h1>
              <p className="text-slate-500 mt-1">Submit and track your insurance claims</p>
            </div>
            <Button onClick={() => setShowSubmitDialog(true)} data-testid="submit-claim-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Claim
            </Button>
          </div>
        </div>

        {/* Claims List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : claims.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Claims Yet</h3>
              <p className="text-slate-500 mb-4">Submit your first insurance claim to get started.</p>
              <Button onClick={() => setShowSubmitDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Submit Claim
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {claims.map(claim => (
              <Card 
                key={claim.claim_id} 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleViewClaim(claim)}
                data-testid={`claim-${claim.claim_id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{claim.claim_reference}</h3>
                        <Badge className={STATUS_COLORS[claim.status]}>
                          {STATUS_NAMES[claim.status]}
                        </Badge>
                      </div>
                      <p className="text-slate-600 mb-2">{getClaimTypeName(claim.claim_type)}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(claim.incident_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          Unit {claim.unit_number}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Submit Claim Dialog */}
        <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Submit Insurance Claim</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Unit Number *</Label>
                  <Input
                    value={newClaim.unit_number}
                    onChange={(e) => setNewClaim({...newClaim, unit_number: e.target.value})}
                    placeholder="e.g., 12A"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Incident Date *</Label>
                  <Input
                    type="date"
                    value={newClaim.incident_date}
                    onChange={(e) => setNewClaim({...newClaim, incident_date: e.target.value})}
                    className="mt-1"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div>
                <Label>Claim Type *</Label>
                <Select
                  value={newClaim.claim_type}
                  onValueChange={(v) => setNewClaim({...newClaim, claim_type: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select claim type" />
                  </SelectTrigger>
                  <SelectContent>
                    {claimTypes.map(type => (
                      <SelectItem key={type.code} value={type.code}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Incident Location</Label>
                <Input
                  value={newClaim.incident_location}
                  onChange={(e) => setNewClaim({...newClaim, incident_location: e.target.value})}
                  placeholder="e.g., Kitchen, Bathroom, Common area"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Incident Description *</Label>
                <Textarea
                  value={newClaim.incident_description}
                  onChange={(e) => setNewClaim({...newClaim, incident_description: e.target.value})}
                  placeholder="Describe what happened in detail..."
                  className="mt-1"
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={newClaim.submitter_phone}
                    onChange={(e) => setNewClaim({...newClaim, submitter_phone: e.target.value})}
                    placeholder="+27..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Estimated Value (R)</Label>
                  <Input
                    type="number"
                    value={newClaim.estimated_value}
                    onChange={(e) => setNewClaim({...newClaim, estimated_value: e.target.value})}
                    placeholder="0.00"
                    className="mt-1"
                  />
                </div>
              </div>

              <p className="text-xs text-slate-500">
                * You can upload photos, videos, and documents after submitting the claim.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmitClaim} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Claim'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Claim Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Claim {selectedClaim?.claim_reference}
                <Badge className={STATUS_COLORS[selectedClaim?.status]}>
                  {STATUS_NAMES[selectedClaim?.status]}
                </Badge>
              </DialogTitle>
            </DialogHeader>
            
            {selectedClaim && (
              <div className="space-y-6 py-4">
                {/* Claim Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Claim Type</p>
                    <p className="font-medium">{getClaimTypeName(selectedClaim.claim_type)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Unit</p>
                    <p className="font-medium">{selectedClaim.unit_number}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Incident Date</p>
                    <p className="font-medium">{formatDate(selectedClaim.incident_date)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Submitted</p>
                    <p className="font-medium">{formatDateTime(selectedClaim.created_at)}</p>
                  </div>
                  {selectedClaim.incident_location && (
                    <div className="col-span-2">
                      <p className="text-slate-500">Location</p>
                      <p className="font-medium">{selectedClaim.incident_location}</p>
                    </div>
                  )}
                  <div className="col-span-2">
                    <p className="text-slate-500">Description</p>
                    <p className="font-medium">{selectedClaim.incident_description}</p>
                  </div>
                  {selectedClaim.assigned_contractor_name && (
                    <div className="col-span-2">
                      <p className="text-slate-500">Assigned Contractor</p>
                      <p className="font-medium flex items-center gap-2">
                        <Wrench className="w-4 h-4" />
                        {selectedClaim.assigned_contractor_name}
                        {selectedClaim.repair_scheduled_date && (
                          <span className="text-slate-500">
                            (Scheduled: {formatDate(selectedClaim.repair_scheduled_date)})
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>

                {/* Upload Section */}
                {selectedClaim.status !== 'claim_closed' && selectedClaim.status !== 'completed' && (
                  <div className="border-t pt-4">
                    <Label className="mb-3 block">Upload Evidence</Label>
                    <div className="flex gap-2">
                      <label className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, 'photo')}
                          disabled={uploadingFile}
                        />
                        <Button variant="outline" className="w-full" asChild disabled={uploadingFile}>
                          <span>
                            <Image className="w-4 h-4 mr-2" />
                            Photo
                          </span>
                        </Button>
                      </label>
                      <label className="flex-1">
                        <input
                          type="file"
                          accept="video/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, 'video')}
                          disabled={uploadingFile}
                        />
                        <Button variant="outline" className="w-full" asChild disabled={uploadingFile}>
                          <span>
                            <Video className="w-4 h-4 mr-2" />
                            Video
                          </span>
                        </Button>
                      </label>
                      <label className="flex-1">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, 'document')}
                          disabled={uploadingFile}
                        />
                        <Button variant="outline" className="w-full" asChild disabled={uploadingFile}>
                          <span>
                            <FileText className="w-4 h-4 mr-2" />
                            Document
                          </span>
                        </Button>
                      </label>
                    </div>
                    {uploadingFile && (
                      <p className="text-sm text-slate-500 mt-2 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Uploading...
                      </p>
                    )}
                  </div>
                )}

                {/* Uploaded Files */}
                {(selectedClaim.photos?.length > 0 || selectedClaim.videos?.length > 0 || selectedClaim.documents?.length > 0) && (
                  <div className="border-t pt-4">
                    <Label className="mb-3 block">Uploaded Files</Label>
                    <div className="space-y-2">
                      {selectedClaim.photos?.map((f, i) => (
                        <a key={i} href={`${API_URL}${f.url}`} target="_blank" rel="noopener noreferrer"
                           className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                          <Image className="w-4 h-4" /> {f.filename}
                        </a>
                      ))}
                      {selectedClaim.videos?.map((f, i) => (
                        <a key={i} href={`${API_URL}${f.url}`} target="_blank" rel="noopener noreferrer"
                           className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                          <Video className="w-4 h-4" /> {f.filename}
                        </a>
                      ))}
                      {selectedClaim.documents?.map((f, i) => (
                        <a key={i} href={`${API_URL}${f.url}`} target="_blank" rel="noopener noreferrer"
                           className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                          <FileText className="w-4 h-4" /> {f.filename}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Photo Gallery Section */}
                {(selectedClaim.photos?.length > 0 || selectedClaim.repair_photos?.length > 0) && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <Label>Photo Gallery</Label>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setShowGalleryDialog(true);
                          setShowDetailDialog(false);
                        }}
                        data-testid="open-gallery-btn"
                      >
                        <ZoomIn className="w-4 h-4 mr-2" />
                        View Gallery
                      </Button>
                    </div>
                    
                    {/* Photo Thumbnails */}
                    <div className="grid grid-cols-4 gap-2">
                      {selectedClaim.photos?.slice(0, 3).map((photo, i) => (
                        <div key={`before-${i}`} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 group">
                          <img 
                            src={`${API_URL}${photo.url}`}
                            alt={`Damage photo ${i + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-red-500/80 text-white text-xs py-1 px-2 text-center">
                            Before
                          </div>
                        </div>
                      ))}
                      {selectedClaim.repair_photos?.slice(0, 3).map((photo, i) => (
                        <div key={`after-${i}`} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 group">
                          <img 
                            src={`${API_URL}${photo.url}`}
                            alt={`Repair photo ${i + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-emerald-500/80 text-white text-xs py-1 px-2 text-center">
                            After
                          </div>
                        </div>
                      ))}
                      {((selectedClaim.photos?.length || 0) + (selectedClaim.repair_photos?.length || 0)) > 6 && (
                        <button
                          onClick={() => {
                            setShowGalleryDialog(true);
                            setShowDetailDialog(false);
                          }}
                          className="aspect-square rounded-lg bg-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-300 transition-colors"
                        >
                          +{(selectedClaim.photos?.length || 0) + (selectedClaim.repair_photos?.length || 0) - 6} more
                        </button>
                      )}
                    </div>
                    
                    {/* Before/After Comparison Available */}
                    {selectedClaim.photos?.length > 0 && selectedClaim.repair_photos?.length > 0 && (
                      <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <p className="text-sm text-emerald-800 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" />
                          <span><strong>Before/After comparison available!</strong> Click "View Gallery" to see the repair results.</span>
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Upload Repair Photos (for completed/in-progress claims) */}
                {(selectedClaim.status === 'repair_in_progress' || selectedClaim.status === 'completed') && 
                 (user?.role === 'trustee' || user?.role === 'property_manager' || user?.role === 'managing_agent' || user?.role === 'platform_owner') && (
                  <div className="border-t pt-4">
                    <Label className="mb-3 block flex items-center gap-2">
                      <Camera className="w-4 h-4 text-emerald-600" />
                      Upload Repair Photos (After)
                    </Label>
                    <p className="text-xs text-slate-500 mb-3">
                      Upload photos showing the completed repairs for before/after comparison.
                    </p>
                    <label>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e, 'repair_photo')}
                        disabled={uploadingFile}
                      />
                      <Button variant="outline" className="w-full" asChild disabled={uploadingFile}>
                        <span className="flex items-center justify-center gap-2">
                          <Camera className="w-4 h-4" />
                          {uploadingFile ? 'Uploading...' : 'Upload Repair Photo'}
                        </span>
                      </Button>
                    </label>
                  </div>
                )}

                {/* Status Timeline */}
                <div className="border-t pt-4">
                  <Label className="mb-3 block">Status Timeline</Label>
                  <div className="space-y-3">
                    {selectedClaim.status_history?.map((entry, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full ${i === 0 ? 'bg-indigo-600' : 'bg-slate-300'}`} />
                          {i < selectedClaim.status_history.length - 1 && (
                            <div className="w-0.5 h-full bg-slate-200 mt-1" />
                          )}
                        </div>
                        <div className="flex-1 pb-4">
                          <p className="font-medium">{STATUS_NAMES[entry.status] || entry.status}</p>
                          <p className="text-sm text-slate-500">{entry.note}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {formatDateTime(entry.timestamp)} • {entry.updated_by}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Photo Gallery Dialog */}
        <Dialog open={showGalleryDialog} onOpenChange={setShowGalleryDialog}>
          <DialogContent className="sm:max-w-4xl max-h-[95vh] overflow-hidden p-0">
            <div className="flex flex-col h-full">
              {/* Gallery Header */}
              <div className="flex items-center justify-between p-4 border-b bg-slate-50">
                <div>
                  <DialogTitle className="text-lg">
                    Photo Gallery - {selectedClaim?.claim_reference}
                  </DialogTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    {selectedClaim?.photos?.length || 0} before • {selectedClaim?.repair_photos?.length || 0} after
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant={galleryView === 'grid' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setGalleryView('grid')}
                    data-testid="gallery-grid-view"
                  >
                    <ImageIcon className="w-4 h-4 mr-1" />
                    Grid
                  </Button>
                  {selectedClaim?.photos?.length > 0 && selectedClaim?.repair_photos?.length > 0 && (
                    <Button 
                      variant={galleryView === 'compare' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={() => setGalleryView('compare')}
                      data-testid="gallery-compare-view"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <ChevronRight className="w-4 h-4 -ml-2" />
                      Compare
                    </Button>
                  )}
                </div>
              </div>

              {/* Gallery Content */}
              <div className="flex-1 overflow-auto p-4">
                {galleryView === 'grid' && (
                  <div className="space-y-6">
                    {/* Before Photos */}
                    {selectedClaim?.photos?.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          Before (Damage Photos)
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                          {selectedClaim.photos.map((photo, i) => (
                            <a 
                              key={i} 
                              href={`${API_URL}${photo.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 group hover:ring-2 hover:ring-red-400 transition-all"
                            >
                              <img 
                                src={`${API_URL}${photo.url}`}
                                alt={`Before ${i + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                              <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                                Before
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* After Photos */}
                    {selectedClaim?.repair_photos?.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                          After (Repair Photos)
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                          {selectedClaim.repair_photos.map((photo, i) => (
                            <a 
                              key={i} 
                              href={`${API_URL}${photo.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 group hover:ring-2 hover:ring-emerald-400 transition-all"
                            >
                              <img 
                                src={`${API_URL}${photo.url}`}
                                alt={`After ${i + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                              <div className="absolute top-2 left-2 bg-emerald-500 text-white text-xs px-2 py-1 rounded">
                                After
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* No photos message */}
                    {(!selectedClaim?.photos?.length && !selectedClaim?.repair_photos?.length) && (
                      <div className="text-center py-12">
                        <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">No photos uploaded yet</p>
                      </div>
                    )}
                  </div>
                )}

                {galleryView === 'compare' && selectedClaim?.photos?.length > 0 && selectedClaim?.repair_photos?.length > 0 && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600 text-center">
                      Drag the slider to compare before and after photos
                    </p>
                    
                    {/* Comparison Slider */}
                    <div 
                      className="relative w-full aspect-video rounded-xl overflow-hidden bg-slate-900 select-none"
                      data-testid="comparison-slider"
                    >
                      {/* After Image (Background) */}
                      <img 
                        src={`${API_URL}${selectedClaim.repair_photos[selectedPhotoIndex % selectedClaim.repair_photos.length]?.url}`}
                        alt="After"
                        className="absolute inset-0 w-full h-full object-contain"
                      />
                      
                      {/* Before Image (Foreground with clip) */}
                      <div 
                        className="absolute inset-0 overflow-hidden"
                        style={{ width: `${comparisonSlider}%` }}
                      >
                        <img 
                          src={`${API_URL}${selectedClaim.photos[selectedPhotoIndex % selectedClaim.photos.length]?.url}`}
                          alt="Before"
                          className="absolute inset-0 w-full h-full object-contain"
                          style={{ width: `${100 / (comparisonSlider / 100)}%`, maxWidth: 'none' }}
                        />
                      </div>
                      
                      {/* Slider Handle */}
                      <div 
                        className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
                        style={{ left: `${comparisonSlider}%`, transform: 'translateX(-50%)' }}
                      >
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
                          <ChevronLeft className="w-4 h-4 text-slate-600" />
                          <ChevronRight className="w-4 h-4 text-slate-600 -ml-1" />
                        </div>
                      </div>
                      
                      {/* Labels */}
                      <div className="absolute top-4 left-4 bg-red-500 text-white text-sm px-3 py-1 rounded-full font-medium">
                        Before
                      </div>
                      <div className="absolute top-4 right-4 bg-emerald-500 text-white text-sm px-3 py-1 rounded-full font-medium">
                        After
                      </div>
                    </div>
                    
                    {/* Slider Control */}
                    <input 
                      type="range" 
                      min="0" 
                      max="100" 
                      value={comparisonSlider}
                      onChange={(e) => setComparisonSlider(parseInt(e.target.value))}
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      data-testid="comparison-range"
                    />
                    
                    {/* Photo Selector */}
                    {(selectedClaim.photos.length > 1 || selectedClaim.repair_photos.length > 1) && (
                      <div className="flex items-center justify-center gap-4">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedPhotoIndex(Math.max(0, selectedPhotoIndex - 1))}
                          disabled={selectedPhotoIndex === 0}
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </Button>
                        <span className="text-sm text-slate-600">
                          Photo {selectedPhotoIndex + 1} of {Math.max(selectedClaim.photos.length, selectedClaim.repair_photos.length)}
                        </span>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedPhotoIndex(selectedPhotoIndex + 1)}
                          disabled={selectedPhotoIndex >= Math.max(selectedClaim.photos.length, selectedClaim.repair_photos.length) - 1}
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Gallery Footer */}
              <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowGalleryDialog(false);
                    setShowDetailDialog(true);
                  }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Claim
                </Button>
                <Button variant="outline" onClick={() => setShowGalleryDialog(false)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
